// Settings → Rules (in Blueprint, Invoices and payments › Signatures and numbers): how invoices and
// estimates are numbered (numbering.get / numbering.set). A prefix and the next number, e.g. INV- and
// 18316, to carry on the company's own sequence; numbers already given stay as they are, and the next
// can't go back over one given (the server refuses).

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import { nextOk, prefixOk } from './numbering-model.ts';

// the checks as typed live in numbering-model.ts, which Blueprint's DocumentNumbers.tsx reads too
export { nextOk, prefixOk };

interface Format {
  prefix: string;
  nextNumber: number;
  next: string;
  lastGiven: string | null;
}
type Kind = 'invoice' | 'estimate';
const LABEL: Record<Kind, string> = { invoice: 'Invoices', estimate: 'Estimates' };

function Row({
  kind,
  format,
  canEdit,
  onSaved,
}: {
  kind: Kind;
  format: Format;
  canEdit: boolean;
  onSaved: () => Promise<void>;
}) {
  const [prefix, setPrefix] = useState(format.prefix);
  const [next, setNext] = useState(String(format.nextNumber));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = prefix !== format.prefix || next.trim() !== String(format.nextNumber);
  const valid = prefixOk(prefix) && nextOk(next);
  const save = useSaveKey(`${kind}:${prefix}:${next}`);
  return (
    <li className="rule numbering">
      <div className="what">
        <b>{LABEL[kind]}</b>
        <p className="muted small">
          {`Next: ${prefixOk(prefix) && nextOk(next) ? `${prefix}${Number(next)}` : format.next}`}
          {format.lastGiven ? ` · last given ${format.lastGiven}` : ''}
        </p>
      </div>
      {!canEdit ? (
        <span className="ro-val">{format.prefix ? `Prefix ${format.prefix}` : 'No prefix'}</span>
      ) : (
        <span className="row">
          <input
            aria-label={`${LABEL[kind]} prefix`}
            className="field prefix"
            value={prefix}
            placeholder="No prefix"
            aria-invalid={!prefixOk(prefix)}
            onChange={(e) => setPrefix(e.target.value)}
          />
          <input
            aria-label={`Next ${kind} number`}
            className="field num"
            inputMode="numeric"
            value={next}
            aria-invalid={!nextOk(next)}
            onChange={(e) => setNext(e.target.value)}
          />
          <SaveButton
            busy={busy}
            busyLabel="Saving…"
            error={error}
            disabled={!changed || !valid}
            className="btn"
            onClick={() =>
              void (async () => {
                setBusy(true);
                setError(null);
                try {
                  await client.call(
                    'numbering.set',
                    { kind, prefix, nextNumber: Number(next) },
                    { idempotencyKey: save.key },
                  );
                  save.renew();
                  await onSaved();
                } catch (e) {
                  setError(e instanceof RpcError || e instanceof Error ? e.message : 'That didn’t save.');
                } finally {
                  setBusy(false);
                }
              })()
            }
          >
            Save
          </SaveButton>
        </span>
      )}
      {!prefixOk(prefix) && <p className="error small">Letters, digits and dashes only, up to 10.</p>}
    </li>
  );
}

/** Document numbers: its own section, or (`bare`) its rows inside another (Signatures and numbers). */
export function NumberingCard({ canEdit, bare = false }: { canEdit: boolean; bare?: boolean }) {
  const [data, setData] = useState<{ invoice: Format; estimate: Format } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = async () => {
    try {
      setData(await client.call('numbering.get', {}));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Couldn’t load the numbers.');
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const rows = (
    <>
      {error && <p className="error">{error}</p>}
      {data && (
        <ul className="rules" aria-label={bare ? 'Document numbers' : undefined}>
          {(['invoice', 'estimate'] as const).map((k) => (
            <Row
              key={`${k}:${data[k].prefix}:${data[k].nextNumber}`}
              kind={k}
              format={data[k]}
              canEdit={canEdit}
              onSaved={load}
            />
          ))}
        </ul>
      )}
    </>
  );
  const about = (
    <p className="muted small">
      Each new invoice and estimate gets the next number, with its prefix: the office, the iPad, the
      customer’s page, texts, emails and PDFs all show it. Numbers already given don’t change, and none is
      ever used twice.
    </p>
  );
  // inside another section, that section's line says who changes them
  if (bare)
    return (
      <>
        {about}
        {rows}
      </>
    );
  return (
    <section className="sheet card" aria-label="Document numbers">
      <h2>Document numbers</h2>
      {about}
      {!canEdit && <p className="muted small read-only">Only the owner or a manager changes these.</p>}
      {rows}
    </section>
  );
}
